import Decimal from 'decimal.js/decimal'

import { dateConstants } from '@/modules/base/util/constants'
import { PriceValues } from '@/modules/moduleSubscription/types/module'
import calculatePrice, { MONTHS_PER_YEAR } from '@/modules/moduleSubscription/components/_helpers/calculatePrice'
import checkDiscount from '@/modules/base/components/modal/upgrade-modal/subscription-selection/_helpers/checkDiscount'

interface PlanPrices {
  name: string
  type?: string
  prices: { period: string, values: PriceValues[] }[]
}

export interface PlanPrice {
  // monthly price without any discount
  regularPricePerMonth: number
  // monthly price while the discount runs - equals the regular price without a discount
  pricePerMonth: number
  discountPercent?: number
  // months the discount covers, undefined when it covers the whole term
  discountMonths?: number
}

const getPriceValues = (plan: PlanPrices, period: string) => plan.prices?.find(price => price.period === period)?.values?.[0]

// months a promotion covers - undefined when it runs forever or has no duration
export const getDiscountMonths = (reducedPrice: PriceValues['reducedPrice']) => {
  const { period, duration } = reducedPrice
  if (period === dateConstants.FOREVER || !duration) return undefined
  return period === dateConstants.MONTH ? duration : duration * MONTHS_PER_YEAR
}

/**
 * Price shown on a plan card: the monthly price of the chosen interval, and - when a promotion
 * applies to the customer - the price during the promotion next to the struck through regular one.
 */
export const getPlanPrice = (plan: PlanPrices, period: string): PlanPrice | null => {
  const priceValues = getPriceValues(plan, period)
  if (!priceValues) return null

  const monthsPerInterval = period === dateConstants.YEAR ? MONTHS_PER_YEAR : 1
  const regularPricePerMonth = new Decimal(priceValues.pricePerDuration).dividedBy(monthsPerInterval).toDP(2).toNumber()
  const { reducedPrice } = priceValues

  if (!reducedPrice?.percent || !checkDiscount(plan.type, reducedPrice)) {
    return { regularPricePerMonth, pricePerMonth: regularPricePerMonth }
  }

  const discountMonths = getDiscountMonths(reducedPrice)
  return {
    regularPricePerMonth,
    pricePerMonth: new Decimal(regularPricePerMonth).times(new Decimal(100).minus(reducedPrice.percent).dividedBy(100)).toDP(2).toNumber(),
    discountPercent: reducedPrice.percent,
    // a promotion covering at least a year needs no duration hint
    discountMonths: discountMonths && discountMonths < MONTHS_PER_YEAR ? discountMonths : undefined,
  }
}

/**
 * The best saving of yearly over monthly billing across all plans, in percent - based on what a year
 * actually costs, i.e. including a promotion. 0 when yearly billing is not cheaper.
 */
export const getMaxYearlySaving = (plans: PlanPrices[]): number => {
  return plans.reduce((max, plan) => {
    const monthValues = getPriceValues(plan, dateConstants.MONTH)
    const yearValues = getPriceValues(plan, dateConstants.YEAR)
    if (!monthValues?.pricePerDuration || !yearValues) return max

    const yearlyTotal = calculatePrice(yearValues, plan.type, dateConstants.YEAR)
    const monthlyTotal = new Decimal(monthValues.pricePerDuration).times(MONTHS_PER_YEAR)
    const saving = new Decimal(1).minus(new Decimal(yearlyTotal).dividedBy(monthlyTotal)).times(100).floor().toNumber()
    return Math.max(max, saving)
  }, 0)
}
